import React from 'react';
import { Link } from 'react-router-dom';

/** The kit every page is built from — the same flat, list-first language as
 *  the campaign portal (ui/campaign-portal/src/components/PlatformBits.tsx;
 *  keep the two in step). No boxed cards: a page is a header, then sections
 *  (a heading over a hairline list or table) separated by whitespace.
 *  Overlays (editors, pickers) use `Modal`. */

export const popover = 'bg-warm-white border border-charcoal/10 rounded-lg shadow-[0_8px_24px_rgba(61,61,61,0.12)]';

export const btn = 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-[13px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
export const btnTone = {
  forest: 'bg-forest text-warm-white hover:bg-forest/90',
  charcoal: 'border border-charcoal/15 text-charcoal hover:bg-charcoal/5',
  brick: 'border border-brick/30 text-brick hover:bg-brick/5',
  ghost: 'text-graphite hover:text-charcoal hover:bg-charcoal/5',
};

/** Text inputs, selects and textareas — one look. */
export const input = 'h-9 px-3 rounded-lg border border-charcoal/15 bg-warm-white text-[13.5px] placeholder:text-graphite/60 focus:outline-none focus:border-forest focus:ring-2 focus:ring-sage/40';
export const label = 'block text-[11px] uppercase tracking-[0.06em] font-semibold text-graphite mb-1.5';

/** Page frame: title, a one-clause subline, a right-side action, an optional
 *  toolbar row (filters, search, breadcrumbs), then sections 40px apart. */
export const PageFrame: React.FC<{
  title: React.ReactNode;
  subline?: React.ReactNode;
  action?: React.ReactNode;
  back?: { to: string; label: string };
  bar?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subline, action, back, bar, children }) => (
  <div className="w-full max-w-[1480px] px-10 pt-8 pb-20">
    {back && (
      <Link to={back.to} className="inline-flex items-center gap-1 text-graphite hover:text-forest font-semibold text-[13px] mb-4">
        <span aria-hidden>←</span> {back.label}
      </Link>
    )}
    <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
      <div className="min-w-0">
        <h1 className="font-display text-[26px] font-semibold tracking-tight leading-tight">{title}</h1>
        {subline && <div className="text-graphite text-[13px] mt-0.5">{subline}</div>}
      </div>
      {action && <div className="flex gap-2">{action}</div>}
    </div>
    {bar && <div className="flex items-center gap-2 flex-wrap mb-8">{bar}</div>}
    <div className="space-y-10">{children}</div>
  </div>
);

/** Page-level loading / error states. */
export const Loading: React.FC<{ what: string }> = ({ what }) => (
  <div className="flex items-center gap-2.5 text-graphite text-[13px] py-10">
    <span className="w-4 h-4 rounded-full border-2 border-sage border-t-forest animate-spin" />Loading {what}…
  </div>
);
export const Failed: React.FC<{ what: string; error: string }> = ({ what, error }) => (
  <div className="rounded-lg bg-brick/5 text-brick px-4 py-3 text-[13.5px]">Couldn't load {what}: {error}</div>
);

export const Pill: React.FC<{ tone?: 'ok' | 'warn' | 'gold' | 'info'; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${
    tone === 'ok' ? 'bg-sage/30 text-forest'
      : tone === 'warn' ? 'bg-brick/10 text-brick'
      : tone === 'gold' ? 'bg-gold/15 text-amber-ink'
      : tone === 'info' ? 'bg-sky-wash/40 text-[#2c6479]'
      : 'bg-charcoal/[0.06] text-graphite'}`}>{children}</span>
);

/** A section: heading + at most one short right-aligned caption or control,
 *  then its list/table straight on the page. `pad` gives non-table children
 *  breathing room under the rule. */
export const Section: React.FC<{ title?: React.ReactNode; note?: React.ReactNode; pad?: boolean; children: React.ReactNode }> = ({ title, note, pad, children }) => (
  <section>
    {title && (
      <div className="flex items-end justify-between gap-3 pb-2.5 flex-wrap">
        <h2 className="font-display text-[17px] font-semibold tracking-tight">{title}</h2>
        {note && <span className="text-graphite text-xs">{note}</span>}
      </div>
    )}
    <div className={`overflow-x-auto border-t border-charcoal/15 ${pad ? 'pt-4' : ''}`}>{children}</div>
  </section>
);

/** The KPI row — each number stacked over its label. `small` is the
 *  compact size used inside a modal. */
export const Kpis: React.FC<{ items: ReadonlyArray<readonly [React.ReactNode, string]>; small?: boolean }> = ({ items, small }) => (
  <div className={`flex flex-wrap gap-y-4 ${small ? 'gap-x-8' : 'gap-x-12'}`}>
    {items.map(([v, l]) => (
      <div key={l}>
        <div className={`num font-semibold leading-none text-charcoal ${small ? 'text-[17px]' : 'font-display text-[26px]'}`}>{v}</div>
        <div className={`${small ? 'mt-1' : 'mt-1.5'} text-[11px] uppercase tracking-[0.06em] font-semibold text-graphite`}>{l}</div>
      </div>
    ))}
  </div>
);

/** The one line under a table that carries a fact the rows can't. */
export const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-3 pt-3 text-[12px] text-graphite">{children}</div>
);

/** One segmented control for every switch and filter. */
export function Tabs<K extends string>({ value, onChange, items }: {
  value: K; onChange: (k: K) => void; items: [K, React.ReactNode][];
}) {
  return (
    <span className="inline-flex rounded-lg bg-charcoal/[0.06] p-0.5">
      {items.map(([k, lbl]) => (
        <button key={k} type="button" onClick={() => onChange(k)}
                className={`px-3 py-1 rounded-md text-[12.5px] font-semibold whitespace-nowrap transition-colors ${
                  value === k ? 'bg-warm-white text-charcoal shadow-[0_1px_2px_rgba(61,61,61,0.15)]' : 'text-graphite hover:text-charcoal'}`}>{lbl}</button>
      ))}
    </span>
  );
}

const th = 'text-[11px] uppercase tracking-[0.06em] font-semibold px-3 pt-3 pb-2.5 border-b border-charcoal/10 whitespace-nowrap';
export const td = 'px-3 py-3 whitespace-nowrap num';
export const tr = 'border-b border-charcoal/[0.06] last:border-b-0 align-middle';
export const trClick = (on = false) => `${tr} cursor-pointer transition-colors ${on ? 'bg-sage/20' : 'hover:bg-charcoal/[0.025]'}`;
/** The selected row's forest bar — on the row's first cell. */
export const picked = (on: boolean) => (on ? 'shadow-[inset_3px_0_0_theme(colors.forest)]' : '');

export const Th: React.FC<{ left?: boolean; className?: string; children?: React.ReactNode }> = ({ left, className = '', children }) => (
  <th className={`${th} ${left ? 'text-left' : 'text-right'} text-graphite ${className}`}>{children}</th>
);

/** Table empty state — one quiet line. */
export const Empty: React.FC<{ cols: number; children: React.ReactNode }> = ({ cols, children }) => (
  <tr><td colSpan={cols} className="text-center text-graphite text-[13px] py-10">{children}</td></tr>
);

/** A media thumbnail for list rows — image, video poster, or a blank tile. */
export const Thumb: React.FC<{ src?: string | null; video?: boolean; className?: string }> = ({ src, video, className = 'w-16 h-11' }) => (
  <span className={`relative ${className} rounded-md overflow-hidden bg-charcoal/[0.06] flex-none block`}>
    {src && (video
      ? <video src={src} muted preload="metadata" className="w-full h-full object-cover" />
      : <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />)}
    {video && <span className="absolute inset-0 grid place-items-center text-warm-white text-[11px] bg-charcoal/25">▶</span>}
  </span>
);

/** The one overlay: a centred panel with a title, scrollable body and a
 *  footer of actions. Only its ✕ (or a Cancel in the footer) closes it —
 *  never a stray backdrop click or Escape, which would drop unsaved edits
 *  and close every stacked modal at once. */
export const Modal: React.FC<{
  title: React.ReactNode;
  onClose: () => void;
  footer?: React.ReactNode;
  wide?: boolean;
  children: React.ReactNode;
}> = ({ title, onClose, footer, wide, children }) => (
    <div className="fixed inset-0 z-50 bg-charcoal/40 grid place-items-center p-6">
      <div className={`${popover} w-full ${wide ? 'max-w-6xl' : 'max-w-2xl'} max-h-[90vh] flex flex-col`}
           role="dialog" aria-modal="true">
        <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-4 border-b border-charcoal/10">
          <h2 className="font-display text-[18px] font-semibold tracking-tight">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close"
                  className="w-8 h-8 grid place-items-center rounded-lg text-graphite hover:text-charcoal hover:bg-charcoal/5">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-charcoal/10">{footer}</div>}
      </div>
    </div>
);
